/**
 * setAccessRequestStatus — RPC contract for the callable Revia staff call
 * from the backoffice to set one access request aside or bring it back.
 *
 * Cross-process contract between the backoffice's access-requests list
 * (caller) and Revia's server `setAccessRequestStatus` handler (onCall).
 * Both ends parse with `.strict()` — an extra key fails the whole request.
 * Granting is `approveAccessRequest`'s, never this call's.
 *
 * The caller holds `STAFF_PERMISSION` in `workspaces/revia`. In one
 * transaction the server moves the request:
 *
 * | stored    | `later`                          | `pending`                        |
 * |-----------|----------------------------------|----------------------------------|
 * | `pending` | moves                            | answers `pending`, writes nothing |
 * | `later`   | answers `later`, writes nothing  | moves                            |
 * | `granted` | failed-precondition              | failed-precondition              |
 *
 * A move stamps `reviewedAt` and `reviewedBy` (the caller's uid) and keeps
 * `expiresAt`: the `UNRESOLVED_RETENTION_MONTHS` run from the requester's
 * last submission, and a staff move does not extend them
 * (`access-request.entity.ts`). It sends no mail.
 *
 * Fields justified:
 *   requestId — the access request's doc id.
 *   status    — the status to move it to.
 *
 * Output:
 *   status — the request's status after the call.
 *
 * Errors (thrown HttpsError, not part of the Output schema):
 *   unauthenticated — no signed-in caller.
 *   permission-denied — the caller does not hold `STAFF_PERMISSION` in
 *     `workspaces/revia`.
 *   not-found — no access request has that id.
 *   failed-precondition — the request is granted.
 */
import { z } from "zod";
import {
	AccessRequestId,
	AccessRequestStatus,
} from "./access-request.entity.ts";

const ReviewStatus = AccessRequestStatus.extract(["later", "pending"]);

export const SetAccessRequestStatusInput = z
	.object({
		requestId: AccessRequestId,
		status: ReviewStatus.describe("the status to move the access request to"),
	})
	.strict();
export type SetAccessRequestStatusInput = z.infer<
	typeof SetAccessRequestStatusInput
>;

export const SetAccessRequestStatusOutput = z
	.object({
		status: ReviewStatus.describe(
			"the access request's status after the call",
		),
	})
	.strict();
export type SetAccessRequestStatusOutput = z.infer<
	typeof SetAccessRequestStatusOutput
>;
